import { useEffect, useRef } from 'react';

const SCRIPT_ID = 'google-identity-services';
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

interface GoogleSignInButtonProps {
  clientId: string;
  /** Recebe o ID token (JWT) emitido pelo Google para este client. */
  onCredential: (credential: string) => void;
  disabled?: boolean;
}

/**
 * Botao oficial do Google Identity Services.
 *
 * O Google renderiza o proprio botao dentro do container e devolve um ID token
 * no callback — quem valida o token e a API. Nao ha secret no browser.
 */
export function GoogleSignInButton({ clientId, onCredential, disabled }: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onCredentialRef = useRef(onCredential);

  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    let cancelled = false;

    function render() {
      const container = containerRef.current;
      if (cancelled || !container || !window.google) return;

      container.innerHTML = '';

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => {
          if (response.credential) onCredentialRef.current(response.credential);
        },
      });

      window.google.accounts.id.renderButton(container, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: '320',
      });
    }

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

    if (existing) {
      if (window.google) render();
      else existing.addEventListener('load', render);

      return () => {
        cancelled = true;
        existing.removeEventListener('load', render);
      };
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', render);
    document.head.appendChild(script);

    return () => {
      cancelled = true;
      script.removeEventListener('load', render);
    };
  }, [clientId]);

  return (
    <div
      ref={containerRef}
      className={`flex justify-center${disabled ? ' pointer-events-none opacity-60' : ''}`}
    />
  );
}
